import { useEffect, useState } from "react";
import { PageNavigation } from "../components/ui/page-navigation";
import { Skeleton } from "../components/ui/skeleton";
import ProfileTab from "../components/profile/ProfileTab";
import LegalTab from "../components/profile/LegalTab";
import { useSelector } from "react-redux";
import { resolveUserNameParts } from "../lib/userName";

const TAB_OPTIONS = [
  { id: "profile", label: "My Profile" },
  { id: "legal", label: "Legal Acknowledgements" },
];

const RETENTION_VALUES = new Set(["1", "7", "30"]);

const normalizeTranscriptRetention = (user = {}) => {
  const directValue = String(user.transcript_purging_time || "").trim();
  if (RETENTION_VALUES.has(directValue)) return directValue;

  const purgingConfig = Array.isArray(user.transcript_purging)
    ? user.transcript_purging[0]
    : null;
  const nestedValue = String(purgingConfig?.time_line || "").trim();
  if (purgingConfig?.enabled !== "no" && RETENTION_VALUES.has(nestedValue)) {
    return nestedValue;
  }

  return "";
};

export default function ProfileSettings() {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const me = useSelector((state) => state.me.me);
  useEffect(() => {
    document.title = "Profile & Settings - Seismic Connect";

    if (me && Object.keys(me).length > 0) {
      setLoading(true);
      setTimeout(() => {
        const { firstName, lastName, fullName } = resolveUserNameParts(me);

        const transcriptRetention = normalizeTranscriptRetention(me);

        setProfileData({
          id: me.doctor_id || me.id || "user-dynamic",
          firstName: firstName || fullName || "",
          middleName: "",
          lastName: lastName,
          primaryEmail: me.email || "",
          secondaryEmail: me.secondaryEmail || "",
          role: me.role || "Doctor",
          accountType: "Clinic",
          npiNumber: me.npiNumber || "",
          specialty: me.specialization || me.specialty || "",
          subSpecialty: "",
          statesOfLicense: me.statesOfLicense || [],
          licenseNumber: me.licenseNumber || "",
          clinicName: me.clinicName || "Unknown Clinic",
          clinicAddress: me.clinicAddress || "",
          clinicId: me.clinicId || "",
          createdAt: me.createdAt || me.created_at || new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          notifications: me.notifications || { email: true, sms: false },
          timeZone: me.timeZone || "America/Los_Angeles",
          transcript_purging_time: transcriptRetention,
          transcript_purging: me.transcript_purging || (
            transcriptRetention
              ? [{ enabled: "yes", time_line: transcriptRetention }]
              : []
          ),
          enable_transcript_purging: transcriptRetention ? "yes" : (me.enable_transcript_purging || "no"),
          security: {
            authenticationMethod: "Single sign-on",
            passwordManagedBy: "Clinic identity provider",
            sessionProtection: "Audit-safe controls enabled",
          }
        });
        setLoading(false);
      }, 400);
    }
  }, [me]);

  useEffect(() => {
    const handler = (event) => {
      if (event.detail?.page === "profile-settings" && event.detail?.tab) {
        setActiveTab(event.detail.tab);
      }
    };
    window.addEventListener("tour-tab-change", handler);
    return () => window.removeEventListener("tour-tab-change", handler);
  }, []);

  return (
    <div className="space-y-4 px-2 pb-4 h-full overflow-y-auto">
      <PageNavigation
        title="Profile & Settings"
        subtitle="Review your registered details and legal acknowledgements."
      />

      <div
        className="mb-6 flex space-x-6 border-b border-gray-200"
        data-tour="settings-tabs"
      >
        {TAB_OPTIONS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-tour={`settings-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`cursor-pointer border-b-2 px-1 pb-4 text-sm font-medium bg-transparent ${
              activeTab === tab.id
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="focus-visible:outline-none focus-visible:ring-0">
        {loading ? (
          <Skeleton className="h-[520px] w-full rounded-xl" />
        ) : activeTab === "profile" ? (
          <ProfileTab profileData={profileData} setProfileData={setProfileData} />
        ) : (
          <LegalTab profileData={profileData} />
        )}
      </div>
    </div>
  );
}
